import React, { useEffect, useState } from 'react'
//@ts-ignore
import { Components, Form } from '@formio/react'
import _ from 'lodash-es'
import {
    BuilderButton,
    ButtonType,
    CustomButton,
    Empty,
    FormTypes,
    InfoTooltip,
    OverlaySpinner,
    ToastTypes,
    getMappingOfForms,
    launchToast
} from '../../../../../components'
import LocalErrorBoundary from '../../../../../components/LocalErrorBoundary'

import cloneDeep from 'lodash/cloneDeep'
import { LiveViewWrapper } from '../../../../../components/formio/forms/attachCustomComponent'
import { useBasicFormSyncStore, useFormSaveStore } from '../../../../../utils'
import { DeviceDataType } from '../../../device-types'
import {
    buildNewDiffConfig,
    getCamerasFromConfig,
    getEnableRawImageControlFromConfig,
    getFramesQueueTriggerSettingsFromConfig,
    getInitialBrigtnessSetting,
    getPeriodicImagesEnableFromConfig,
    getResetHistoryValuesFromConfig,
    getSnoozeMinutesFromConfig
} from './utils'
import { useLiveViewStateStore } from '../../../../LiveView/LiveViewStore'
import styles from './ScannerBasicConfig.module.css'

Components.setComponents({
    liveView: LiveViewWrapper
})

interface BasicConfigProps {
    deviceData: DeviceDataType | undefined
    selectedDevice: any // Replace 'any' with the appropriate type for selected device
    editDiffConfig: (deviceId: string, config: any) => void // Replace 'any' with the appropriate type for config
    setDeviceForceUpdateAction: (deviceId: string) => void
    loadingSaveBasicConfig: boolean
    syncCurrentConfig?: (config: any) => void
    standalone?: boolean
}

export const ScannerBasicConfig = ({
    deviceData,
    selectedDevice,
    editDiffConfig,
    setDeviceForceUpdateAction,
    loadingSaveBasicConfig,
    standalone = false
}: BasicConfigProps) => {
    const { base, diff } = deviceData?.config || {}
    const merged = _.merge({}, base, diff)
    const [currentSubmission, setCurrentSubmission] = useState<any>(merged) // Replace 'any' with the correct type
    const isValid = React.useRef<boolean>(true)
    const hasChanges = useFormSaveStore((state) => state.hasChanges)
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const setSyncedBasicConfigState = useBasicFormSyncStore((state: { setSyncState: any }) => state.setSyncState)
    const { baseURL, transferZoomAndFocusToLiveView } = useLiveViewStateStore()

    useEffect(() => {
        setCurrentSubmission(merged)
        //eslint-disable-next-line
    }, [selectedDevice])

    if (!deviceData) {
        return <Empty title="Select a device first!" />
    }

    const forceUpdateDisabled =
        !!deviceData?.config_force_update ||
        deviceData?.config_current_timestamp === deviceData?.config_target_timestamp

    // const form = getMappingOfForms.DeviceBasicConfig.currentForm
    const form = getMappingOfForms.DeviceBasicConfigV2.currentForm

    const cameras = getCamerasFromConfig(base, diff)

    const onSubmit = (_submission: any) => {
        console.log(_submission)
    }

    const onChange = (data: any) => {
        isValid.current = data.isValid

        if (data.changed) {
            // console.log(data)
            setCurrentSubmission(data.data)
            if (!data.changed.flags.fromSubmission) {
                setFormChanges(true)
                try {
                    // console.trace('Scanner config triggered', data.changed?.component?.label)
                } catch (error) {
                    console.log(error)
                }
                setSyncedBasicConfigState(
                    buildNewDiffConfig(cloneDeep(diff), cloneDeep(base), cloneDeep(data.data), cloneDeep(merged))
                )
            }
            if (
                (data.data.changed.camera.zoom_value !== Number(localStorage.getItem('zoom')) ||
                    data.data.changed.camera.focus_value !== Number(localStorage.getItem('focus'))) &&
                data.data.reset11 === true
            ) {
                transferZoomAndFocusToLiveView({
                    zoom: data.data.changed.camera.zoom_value,
                    focus: data.data.changed.camera.focus_value
                })
                data.data.reset11 = false
                // console.log('sent', data.data.changed.camera.zoom_value, data.data.changed.camera.focus_value)
                // console.log(data)
            }
        }
    }

    const formComp = (
        <Form
            submission={{
                data: {
                    changed: {
                        pipeline_mode: merged?.vehicle_detection?.pipeline_mode,
                        enable_video_recording: merged?.video_recording?.enable,
                        scanner_upload_render: merged?.vehicle_detection?.scanner?.upload_render || false,
                        brightnessSettings: getInitialBrigtnessSetting(cameras?.[0]),
                        snooze_minutes: getSnoozeMinutesFromConfig(base, diff),
                        reset_history_values: getResetHistoryValuesFromConfig(base, diff),
                        enable_raw_image_control: getEnableRawImageControlFromConfig(base, diff),
                        enablePeriodicImages: getPeriodicImagesEnableFromConfig(base, diff),
                        framesQueueTriggerSettings: getFramesQueueTriggerSettingsFromConfig(base, diff),
                        camera: { ...cameras?.[0] },
                        ...currentSubmission.changed
                    },
                    selectValues: {
                        standalone: standalone,
                        base,
                        cameras: cameras,
                        selectedCamera: cameras?.[0],
                        changedCamera: {}
                    }
                }
            }}
            form={form}
            onSubmit={onSubmit}
            onChange={onChange}
        />
    )

    if (!base.vehicle_detection) {
        return <>Device Not supported</>
    }

    return (
        <div className={styles.root}>
            {!standalone && (
                <div className={styles.actions}>
                    <BuilderButton formName={FormTypes.DeviceBasicConfigV2} />
                    &nbsp;
                    <CustomButton
                        type={ButtonType.SAVE}
                        disabled={!hasChanges}
                        onClick={() => {
                            if (!isValid.current) {
                                launchToast(ToastTypes.Error, 'Please fill all the errors in the form first!')
                                return
                            }

                            setFormChanges(false)
                            editDiffConfig(
                                selectedDevice?.uuid,
                                buildNewDiffConfig(
                                    cloneDeep(diff),
                                    cloneDeep(base),
                                    cloneDeep(currentSubmission),
                                    cloneDeep(merged)
                                )
                            )
                        }}
                    >
                        Save Configuration
                    </CustomButton>
                    &nbsp;
                    <InfoTooltip content={forceUpdateDisabled ? 'No changes to force update' : null}>
                        <CustomButton
                            type={ButtonType.DELETE}
                            disabled={forceUpdateDisabled}
                            onClick={() => {
                                if (
                                    window.confirm(
                                        'Are you sure you want to force update the configuration for this device?'
                                    )
                                ) {
                                    setDeviceForceUpdateAction(selectedDevice?.uuid)
                                }
                            }}
                        >
                            Force Update
                        </CustomButton>
                    </InfoTooltip>
                    &nbsp;
                </div>
            )}
            <OverlaySpinner loadingTop={true} loading={loadingSaveBasicConfig}>
                <div className="bootstrap-scope">
                    <LocalErrorBoundary tag="ScannerBasicConfig">{formComp}</LocalErrorBoundary>
                </div>
            </OverlaySpinner>
        </div>
    )
}
